import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { LeadsService } from '@/data/leads.service';
import { DealsService } from '@/data/deals.service';
import { EventsService } from '@/data/events.service';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/errors';
import { logger } from '@/lib/logger';
import { completeLead } from '@/lib/leads/actions';
import type { ActionOutcome } from '@/components/today/ActionOutcomeModal';
import type { NextStepType } from '@/components/today/NextStepModal';
import type { DealType } from '@/components/today/CloseDealModal';

interface CompleteActionInput {
  leadId: string;
  actionType: 'call' | 'whatsapp' | 'email' | 'meeting';
  outcome: ActionOutcome;
  nextStep: {
    type: NextStepType;
    date?: Date;
    stage?: string;
  };
  closeDealData?: {
    dealId?: string;
    deal_value: number;
    deal_type: DealType;
    close_reason?: string;
  };
}

/**
 * Hook to complete an action with outcome and next step
 * This is the Core Loop: Action -> Outcome -> Next Step -> Activity Log
 */
export function useCompleteAction() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CompleteActionInput) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      if (!user?.id) throw new Error('No user');

      const { leadId, actionType, outcome, nextStep, closeDealData } = input;

      // 1. Get current lead
      const scope = { type: 'single' as const, workspaceId: activeWorkspaceId };
      const lead = await LeadsService.getLead(leadId, scope);
      if (!lead) throw new Error('Lead not found');

      // 2. Validate Won/Lost requires closeDealData
      if ((outcome === 'won' || outcome === 'lost') && !closeDealData) {
        throw new Error('Deal value and type are required to close a deal');
      }

      // 2b. "No next step" / Mark as complete → completeLead (COMPLETED, not LOST)
      // 2c. "Not fit / don't contact" → completeLead with opt_out (DONE, not LOST); skip NextStepModal
      if (nextStep.type === 'none' || outcome === 'not_fit') {
        await completeLead(leadId, activeWorkspaceId, outcome === 'not_fit' ? { optOut: true } : undefined);
        try {
          const { error: activityError } = await supabase.rpc('log_lead_activity', {
            p_workspace_id: activeWorkspaceId,
            p_lead_id: leadId,
            p_user_id: user.id,
            p_type: actionType,
            p_channel: actionType,
            p_outcome: outcome,
            p_next_step_type: 'none',
            p_next_step_date: null,
            p_notes: null,
            p_meta: { actionType, outcome, nextStepType: 'none', nextStepDate: null, stage: null },
          });
          if (activityError) {
            logger.warn('[useCompleteAction] RPC failed (mark complete)', activityError);
            await supabase.from('lead_activities').insert({
              workspace_id: activeWorkspaceId,
              lead_id: leadId,
              user_id: user.id,
              type: actionType,
              channel: actionType,
              outcome,
              next_step_type: 'none',
              next_step_date: null,
              meta: { actionType, outcome, nextStepType: 'none', nextStepDate: null, stage: null },
            } as any);
          }
        } catch (err: any) {
          if (err?.code !== '42P01' && !err?.message?.includes('does not exist')) {
            logger.error('[useCompleteAction] Failed to log activity (mark complete)', err, { leadId });
          }
        }
        return { leadId, updates: { is_completed: true, completed_at: new Date().toISOString() } };
      }

      // 3. Update lead fields based on outcome and next step
      const now = new Date();
      const updates: any = {
        last_interaction_at: now.toISOString(),
        last_contacted_at: now.toISOString(),
      };

      // Handle next step (skip for Won/Lost)
      if (outcome !== 'won' && outcome !== 'lost') {
        if (nextStep.type === 'follow_up_2d' || nextStep.type === 'follow_up_7d') {
          updates.next_follow_up_at = nextStep.date?.toISOString() || null;
        } else if (nextStep.type === 'schedule_meeting' && nextStep.date) {
          updates.next_follow_up_at = nextStep.date.toISOString();
        } else {
          updates.next_follow_up_at = null;
        }
      } else {
        // No next step for closed deals
        updates.next_follow_up_at = null;
      }

      // Handle stage change (use exact enum value, no normalisation)
      if (nextStep.type === 'move_stage' && nextStep.stage != null && nextStep.stage !== '') {
        updates.pipeline_stage = nextStep.stage;
      }

      // Handle outcome-specific updates
      if (outcome === 'won') {
        updates.pipeline_stage = 'won';
        updates.status = 'won';
      } else if (outcome === 'lost') {
        updates.pipeline_stage = 'lost';
        updates.status = 'lost';
        if (closeDealData?.close_reason) {
          updates.lost_reason = closeDealData.close_reason;
        }
      }
      // not_fit is handled above (completeLead with optOut); never set status = 'not_fit'

      // 4. Update lead
      await LeadsService.updateLead({
        id: leadId,
        workspace_id: activeWorkspaceId,
        ...updates,
      });

      // 5. Handle deal creation/update for Won/Lost
      if ((outcome === 'won' || outcome === 'lost') && closeDealData) {
        const { dealId, deal_value, deal_type, close_reason } = closeDealData;
        
        if (dealId) {
          // Update existing deal
          const updatedDeal = await DealsService.updateDeal({
            id: dealId,
            workspace_id: activeWorkspaceId,
            stage: outcome === 'won' ? 'won' : 'lost',
            value: deal_value,
            deal_type: deal_type,
            closed_at: now.toISOString(),
            loss_reason: outcome === 'lost' ? close_reason : null,
          });
          
          // Delete notifications when deal is won/lost
          try {
            const { deleteDealNotifications } = await import('@/lib/dealNotifications');
            await deleteDealNotifications(dealId, activeWorkspaceId);
          } catch (error) {
            // Non-blocking
            console.warn('[useCompleteAction] Failed to delete notifications', error);
          }
        } else {
          // Create new deal if it doesn't exist (title required by DB)
          const title =
            (lead as any)?.company?.trim() ||
            (lead as any)?.lead_name?.trim() ||
            (lead as any)?.name?.trim() ||
            `Deal ${leadId}`.trim() ||
            'Untitled deal';
          if (!title || title.length === 0) {
            toast.error('Missing deal title');
            throw new Error('Missing deal title');
          }
          await DealsService.createDeal({
            workspace_id: activeWorkspaceId,
            lead_id: leadId,
            title,
            stage: outcome === 'won' ? 'won' : 'lost',
            value: deal_value,
            deal_type: deal_type,
            closed_at: now.toISOString(),
            loss_reason: outcome === 'lost' ? close_reason : null,
          });
        }
      }

      // 6. Create calendar event if next step is scheduled (skip for Won/Lost)
      if (outcome !== 'won' && outcome !== 'lost' && nextStep.date && (nextStep.type === 'schedule_meeting' || nextStep.type === 'follow_up_2d' || nextStep.type === 'follow_up_7d')) {
        try {
          const endDate = new Date(nextStep.date);
          endDate.setHours(endDate.getHours() + 1); // 1 hour duration

          await EventsService.createEvent({
            workspace_id: activeWorkspaceId,
            title: `Follow up: ${lead.name}`,
            type: 'meeting',
            start_at: nextStep.date.toISOString(),
            end_at: endDate.toISOString(),
            lead_id: leadId,
            priority: 'normal',
            reminder_offsets_minutes: [60, 15, 0],
          });
        } catch (error) {
          logger.error('[useCompleteAction] Failed to create calendar event', error, { leadId, workspaceId: activeWorkspaceId });
          // Don't fail the whole operation if event creation fails
        }
      }

      // 7. Log activity (using RPC function or direct insert)
      try {
        const { error: activityError } = await supabase.rpc('log_lead_activity', {
          p_workspace_id: activeWorkspaceId,
          p_lead_id: leadId,
          p_user_id: user.id,
          p_type: actionType,
          p_channel: actionType,
          p_outcome: outcome,
          p_next_step_type: nextStep.type,
          p_next_step_date: nextStep.date?.toISOString() || null,
          p_notes: null,
          p_meta: {
            actionType,
            outcome,
            nextStepType: nextStep.type,
            nextStepDate: nextStep.date?.toISOString() || null,
            stage: nextStep.stage || null,
          },
        });

        if (activityError) {
          // Fallback to direct insert if RPC doesn't exist
          logger.warn('[useCompleteAction] RPC failed, trying direct insert', activityError);
          await supabase.from('lead_activities').insert({
            workspace_id: activeWorkspaceId,
            lead_id: leadId,
            user_id: user.id,
            type: actionType,
            channel: actionType,
            outcome,
            next_step_type: nextStep.type,
            next_step_date: nextStep.date?.toISOString() || null,
            meta: {
              actionType,
              outcome,
              nextStepType: nextStep.type,
              nextStepDate: nextStep.date?.toISOString() || null,
              stage: nextStep.stage || null,
            },
          } as any);
        }
      } catch (error: any) {
        // If activity log fails, log warning but don't fail the operation
        if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
          logger.warn('[useCompleteAction] lead_activities table does not exist, skipping log', { leadId, workspaceId: activeWorkspaceId });
        } else {
          logger.error('[useCompleteAction] Failed to log activity', error, { leadId, workspaceId: activeWorkspaceId });
        }
      }

      return { leadId, updates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['deals'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics'], exact: false });
      toast.success('Action completed');
    },
    onError: (error: any) => {
      logger.error('[useCompleteAction] Failed to complete action', error);
      toast.error(errorMessage(error, 'Failed to complete action'));
    },
  });
}

